import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual, isEmpty } from 'lodash';
import { defaultNotifier } from 'functions/notificationHandler';
import Card from 'components/Detail/Card';
import LineChart from '../charts/LineChart';
import AreaChart from '../charts/AreaChart';
import BarChart from '../charts/BarChart';
import TableChart from '../charts/TableChart';
import CardChart from '../charts/CardChart';
import PieChart from '../charts/PieChart';
import DoughnutChart from '../charts/DoughnutChart';
import GaugeChart from '../charts/GaugeChart';
import HeatmapChart from '../charts/Heatmap';
import MapChart from '../charts/MapChart';
import AiInsightChart from '../charts/AiInsightChart';
import { FilterDate } from './inputs/FilterDate';
import { formatDistance } from 'date-fns';
import { EnumType } from 'json-to-graphql-query';
import {
  CHART_TYPE_LINE,
  CHART_TYPE_AREA,
  CHART_TYPE_BAR,
  CHART_TYPE_PIE,
  CHART_TYPE_DOUGHNUT,
  CHART_TYPE_GAUGE,
  CHART_TYPE_TABLE,
  CHART_TYPE_CARD,
  CHART_TYPE_HEATMAP,
  CHART_TYPE_MAP,
  CHART_TYPE_INSIGHTS,
} from '../chartConfigurations';
import { customFormatDistance } from '../charts/utils';
import { getDefaultDateOptions, RELATIVE_TIME_RANGE_OPTIONS } from './inputs/DatePicker'


const relativeTimeOptions = [...RELATIVE_TIME_RANGE_OPTIONS];

const getDefaultDate = () => {
  return getDefaultDateOptions(false, relativeTimeOptions).find(date => date.label === 'Last 1 month') || {}
}

class Preview extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dashboardDevices: [],
      dashboardTimeRange: { ...getDefaultDate() },
      previewChart: {
        type: '',
        title: '',
        tableName: '',
        metricColumns: [],
        configuration: {}
      },
      previewParams: {
        dataPoint: '',
        intervalOption: '',
        intervalQuantity: '',
        groupByColumns: [],
        visoDevices: [],
        timeRange: {
          start: '',
          end: ''
        }
      },
      chartHeight: 300,
      dataStreams: []
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  async componentDidMount() {
    const { chartValues, dashboardTimeRange } = this.props;

    // this.props.changeChartValue('dataStreams', {...chartValues.dataStreams, streams: chartValues.dataStreams.streams.map(stream => ({ ...stream, streamLabel: {...stream.streamLabel, value: `${stream.streamLabel?.value} `} }))})

    // TODO set a default dashboard time range
    if (this.state.dashboardTimeRange?.start) {
      return this.onFilterDateApply(this.state.dashboardTimeRange)
    }


    if (this.props.dashboardDate?.startDate) {
      const newDashboardDate = {
        ...this.props.dashboardDate,
        start: this.props.dashboardDate?.startDate,
        end: this.props.dashboardDate?.endDate
      }
      return this.onFilterDateApply(newDashboardDate)
    }

    if (this.props.dashboardDate?.start) {
      const newDashboardDate = {
        ...this.props.dashboardDate,
        start: this.props.dashboardDate?.start,
        end: this.props.dashboardDate?.end
      }
      
      return this.onFilterDateApply(newDashboardDate)
    }

  }

  async componentDidUpdate(prevProps, prevState) {
    if (!isEqual(this.props, prevProps)) {
      this.setPreviewData()
    }

    const currentGroupByColumns = this.props.groupByColumns
    const prevGroupByColumns = prevProps.groupByColumns
    if (!isEqual(currentGroupByColumns, prevGroupByColumns)) {
      this.setPreviewData()
    }

    const currentDashboardTimeRange = this.state.dashboardTimeRange
    const prevDashboardTimeRange = prevState.dashboardTimeRange
    if (!isEqual(currentDashboardTimeRange, prevDashboardTimeRange)) {
      this.setPreviewData()
    }
  }

  setChartHeight = () => {
    // TODO set chart height form chart components (does it depends in chart type?)
  }

  setPreviewData = () => {
    const {
      chartType,
      chartConfig,
      tableName,
      metricColumns,
      dataPoint,
      metricAggregations,
      intervalOption,
      intervalQuantity,
      groupByColumns,
      visoDevices,
      timeRange,
      dataStreams,
      chartValues
    } = this.props

    const chartTitle = chartConfig?.basicFields?.find(item => item.id === 'title')?.value || ''

    this.setState({
      previewChart: {
        ...this.state.previewChart,
        type: chartType,
        title: chartTitle,
        tableName: tableName,
        metricColumns: metricColumns,
        configuration: chartConfig
      },
      previewParams: {
        ...this.state.previewParams,
        dataPoint: dataPoint ? new EnumType(dataPoint) : null,
        metricAggregations: !isEmpty(metricAggregations)
          ? metricAggregations.map(metricAggr => ({ ...metricAggr, dataPoint: new EnumType(metricAggr.dataPoint) }))
          : metricAggregations,
        intervalOption: new EnumType(intervalOption),
        intervalQuantity: parseInt(intervalQuantity, 10),
        groupByColumns: groupByColumns,
        visoDevices: visoDevices || [],
        timeRange: this.state.dashboardTimeRange
      },
      dataStreams: [...dataStreams]
    }, () => {

    })
  }

  setTimeRange = () => {
    // TODO You just need to pass the dashboardTimeRange and the configuration where there is the chart time range. 
    // Chart should be in charge of getting the right time range (Priority is the chart time range if empty then get the dashboard time range)
  }

  getTimeRangeLabel = () => {
    if (this.state.dashboardTimeRange?.label) {
      return this.state.dashboardTimeRange.label
    }

    if (this.state.dashboardTimeRange?.end) {
      return this.state.dashboardTimeRange.end
    }

    if (this.props.dashboardDate.startDate) {
      const estimatedTime = customFormatDistance(formatDistance((this.props.dashboardDate.startDate), this.props.dashboardDate.endDate))

      return estimatedTime
    }

  }

  getTimeRange = () => {
    if (this.state.dashboardTimeRange.start && this.state.dashboardTimeRange.end) {
      return {
        startDate: this.state.dashboardTimeRange.start,
        endDate: this.state.dashboardTimeRange.end
      }
    }

    if (this.props.dashboardDate.startDate && this.props.dashboardDate.endDate) {
      return {
        startDate: this.props.dashboardDate.startDate,
        endDate: this.props.dashboardDate.endDate
      }
    }
  }

  onFilterDateApply = (data) => {
    this.setState({
      isLoading: true,
      dashboardTimeRange: { ...data }
    })

    this.props.changeChartValue('dateRange', data)

    setTimeout(() => {
      this.setState({ isLoading: false })
    }, 1000);
  }

  onRefresh = () => {
    this.setState({ isLoading: true })
    this.setPreviewData()
    setTimeout(() => {
      this.setState({ isLoading: false })
    }, 1000)
  }



  renderContent = () => {
    return (
      <div className='chart-editor__preview'>

        {!this.props.blankPreview && (this.props.chartType !== CHART_TYPE_INSIGHTS) &&
          <div className='filters-bar'>
            <FilterDate
              menuOffset='center'
              label={this.state.dashboardTimeRange.label}
              selectedDate={this.state.dashboardTimeRange}
              defaultDate={getDefaultDate()}
              onChange={value => this.onFilterDateApply(value)}
            />
          </div>
        }

        <React.Fragment>
          {!this.props.chartType &&
            <Card
              noGrid
              className="overview chart-cards charts-card-preview"
              height={250}
              content={<div className="chart-error"><div className='viso-text-label'>Select a chart type to display the preview</div></div>}
            />
          }
          {this.props.chartType === CHART_TYPE_LINE &&
            <LineChart
              isPreview={true}
              chartType={this.props.chartType}
              color={this.props.color}
              sqlError={this.props.sqlError}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              previewParams={this.state.previewParams}
              devicesData={this.props.devicesData}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_AREA &&
            <AreaChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              devicesData={this.props.devicesData}
              refreshPreview={this.props.refreshPreview}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_BAR &&
            <BarChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              devicesData={this.props.devicesData}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_TABLE &&
            <TableChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              devicesData={this.props.devicesData}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_CARD &&
            <div>
              <CardChart
                isPreview={true}
                chartType={this.props.chartType}
                sqlError={this.props.sqlError}
                color={this.props.color}
                isNewVersion={this.props.isNewVersion}
                refreshPreview={this.props.refreshPreview}
                devicesData={this.props.devicesData}
                previewParams={this.state.previewParams}
                chart={this.state.previewChart}
                height={200}
                dashboardDevices={this.state.dashboardDevices}
                dashboardTimeRange={this.state.dashboardTimeRange}
                chartValues={this.props.chartValues}
                dashboardId={this.props.dashboardId}
                dataStreams={this.props.dataStreams}
              id={this.props.previewId}
              />
            </div>
          }
          {this.props.chartType === CHART_TYPE_PIE &&
            <PieChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              previewParams={this.state.previewParams}
              devicesData={this.props.devicesData}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_DOUGHNUT &&
            <DoughnutChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              devicesData={this.props.devicesData}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_GAUGE &&
            <GaugeChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              devicesData={this.props.devicesData}
              refreshPreview={this.props.refreshPreview}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              id={this.props.previewId}
            />
          }
          {this.props.chartType === CHART_TYPE_HEATMAP &&
            <HeatmapChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              devicesData={this.props.devicesData}
              refreshPreview={this.props.refreshPreview}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={this.state.chartHeight}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
            />
          }
          {this.props.chartType === CHART_TYPE_MAP &&
            <MapChart
              isPreview={true}
              chartType={this.props.chartType}
              sqlError={this.props.sqlError}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              devicesData={this.props.devicesData}
              previewParams={this.state.previewParams}
              chart={this.state.previewChart}
              height={400}
              dashboardDevices={this.state.dashboardDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              dataStreams={this.props.dataStreams}
              locations={this.props.locations}
            />
          }
          {this.props.chartType === CHART_TYPE_INSIGHTS &&
            <AiInsightChart
              isPreview={true}
              chartType={this.props.chartType}
              color={this.props.color}
              isNewVersion={this.props.isNewVersion}
              refreshPreview={this.props.refreshPreview}
              chart={this.state.previewChart}
              height={400}
              data={this.props.chartInsightsData}
              allDevices={this.props.allDevices}
              dashboardTimeRange={this.state.dashboardTimeRange}
              chartValues={this.props.chartValues}
              dashboardId={this.props.dashboardId}
              detailRole={this.props.detailRole}
              hasPermissionToAccessAiInsight={this.props.workspace?.features?.aiInsights?.enabled}
            />
          }
        </React.Fragment>
      </div>
    );
  }

  render() {
    return (
      <Card
        noGrid
        color={this.props.color}
        title={!this.props.blankPreview && 'Preview'}
        titleClass="pd-b_15"
        isLoading={this.props.isLoading}
        className={this.props.blankPreview ? "content-box__nopadding no-shadow" : "overview content-box__grey no-shadow"}
        content={this.renderContent()}
      />
    );
  }
}

Preview.propTypes = {
  onEdit: PropTypes.func,
  dataset: PropTypes.object,

  chartType: PropTypes.string,
  chartConfig: PropTypes.object,
  tableName: PropTypes.any,
  metricColumns: PropTypes.any,
  dataPoint: PropTypes.any,
  metricAggregations: PropTypes.array,
  intervalOption: PropTypes.any,
  intervalQuantity: PropTypes.any,
  groupByColumns: PropTypes.any,
  visoDevices: PropTypes.any,
  timeRange: PropTypes.any,
  isLoading: PropTypes.any,
  color: PropTypes.any,
  refreshPreview: PropTypes.bool,
  dashboardId: PropTypes.string,
  dataStreams: PropTypes.array
};

export default Preview;
